import { useState } from "react";
import {
  Settings,
  Moon,
  Sun,
  Monitor,
  User,
  Bell,
  Shield,
  Sparkles,
  Lock,
  Check,
  FlaskConical,
  Eye,
  EyeOff,
  RotateCcw,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { APPS } from "@/data/apps";
import { useHive } from "@/store/useHive";
import { useAuth } from "@/auth/AuthProvider";
import { rehydrateOrgData } from "@/lib/orgs";
import { AppHeader, Card, SectionTitle } from "@/components/ui/primitives";
import { Modal } from "@/components/ui/Modal";
import { PRODUCT_IDS, productState } from "@/lib/entitlements";
import { cn } from "@/lib/utils";

const accent = APPS.settings.accent;

const STATE_LABEL: Record<string, string> = {
  enabled: "Enabled",
  preview: "Early Access preview",
  locked: "Early Access",
};

/** Per-product entitlement rows — read-only state + demand capture. */
function ProductsCard() {
  const entitlements = useHive((s) => s.entitlements);
  const earlyAccessMine = useHive((s) => s.earlyAccessMine);
  const openProductPreview = useHive((s) => s.openProductPreview);

  const rows = PRODUCT_IDS.map((p) => ({ p, state: productState(entitlements, p) })).filter(
    (r) => r.state !== "hidden",
  );

  return (
    <Card className="p-5">
      <SectionTitle>Products</SectionTitle>
      <div className="space-y-2">
        {rows.map(({ p, state }) => {
          const app = APPS[p];
          const requested = !!earlyAccessMine[p];
          return (
            <div key={p} className="flex items-center gap-3 rounded-lg border border-app bg-surface-2/40 p-2.5">
              <span
                className="grid h-7 w-7 place-items-center rounded-md text-white"
                style={{ background: `linear-gradient(135deg, ${app.accent}, ${app.accent}aa)` }}
              >
                <app.icon size={14} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-app">{app.name}</span>
                <span className="block text-xs text-app-2">{STATE_LABEL[state] ?? state}</span>
              </span>
              {state === "enabled" ? (
                <Check size={14} className="text-emerald-500" />
              ) : requested ? (
                <span className="flex items-center gap-1 text-[11px] font-medium text-app-2">
                  <Check size={12} className="text-emerald-500" /> On the list
                </span>
              ) : (
                <button
                  onClick={() => openProductPreview(p)}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-black transition hover:brightness-105"
                  style={{ background: app.accent }}
                >
                  {state === "locked" ? <Lock size={11} /> : <Sparkles size={11} />} Early access
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/** Sample-data lifecycle: visibility toggle, restore, permanent delete. */
function SampleDataCard() {
  const sampleData = useHive((s) => s.sampleData);
  const seedSampleData = useHive((s) => s.seedSampleData);
  const deleteSampleData = useHive((s) => s.deleteSampleData);
  const setSampleVisibility = useHive((s) => s.setSampleVisibility);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const present = !!sampleData?.present;
  const visible = !!sampleData?.visible;
  const total = sampleData
    ? Object.values(sampleData.counts).reduce((a, b) => a + b, 0)
    : 0;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="relative p-5">
      <SectionTitle>Sample data</SectionTitle>
      <p className="mb-3 text-xs leading-relaxed text-app-2">
        A realistic demo dataset (workspaces, tasks, workflows, runs) seeded into your
        organization. Sample records are always labelled, never trigger real actions, and
        never leave Hive — they're excluded from every external surface.
      </p>

      {present ? (
        <>
          <div className="mb-3 flex items-center justify-between rounded-lg border border-app bg-surface-2/40 px-3 py-2">
            <span className="flex items-center gap-2 text-xs text-app-1">
              <FlaskConical size={13} className="text-amber-500" />
              {total} sample records
            </span>
            <button
              disabled={busy}
              onClick={() => void run(() => setSampleVisibility(!visible))}
              className="flex items-center gap-1.5 rounded-lg border border-app px-2.5 py-1 text-[11px] font-medium text-app-1 transition hover:border-app-strong disabled:opacity-50"
            >
              {visible ? <EyeOff size={12} /> : <Eye size={12} />}
              {visible ? "Hide sample data" : "Show sample data"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              disabled={busy}
              onClick={() => void run(seedSampleData)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-app px-3 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong disabled:opacity-50"
            >
              <RotateCcw size={12} /> Restore sample data
            </button>
            <button
              disabled={busy}
              onClick={() => setConfirmOpen(true)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-rose-500/40 px-3 py-1.5 text-[11px] font-medium text-rose-500 transition hover:bg-rose-500/10 disabled:opacity-50"
            >
              <Trash2 size={12} /> Delete permanently
            </button>
          </div>
        </>
      ) : (
        <button
          disabled={busy}
          onClick={() => void run(seedSampleData)}
          className="flex items-center justify-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105 disabled:opacity-50"
          style={{ background: accent }}
        >
          <FlaskConical size={13} /> Explore with sample data
        </button>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete sample data permanently?"
        subtitle={`${total} sample records will be hard-deleted`}
        icon={<AlertTriangle size={16} />}
        accent="#f43f5e"
        width={420}
      >
        <p className="text-xs leading-relaxed text-app-1">
          This removes every sample workspace, task, integration, workflow, run and
          approval from your organization. Real records are untouched. You can re-seed a
          fresh copy with “Restore sample data” at any time.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setConfirmOpen(false)}
            className="rounded-lg border border-app px-3 py-1.5 text-xs font-medium text-app-1 transition hover:border-app-strong"
          >
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={() => {
              setConfirmOpen(false);
              void run(deleteSampleData);
            }}
            className="rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
          >
            Delete permanently
          </button>
        </div>
      </Modal>
    </Card>
  );
}

export function SettingsApp() {
  const { theme, toggleTheme, pushToast } = useHive();
  const { me, switchOrg } = useAuth();
  return (
    <div className="min-h-full">
      <AppHeader icon={Settings} title="Settings" subtitle="System preferences" accent={accent} />

      <div className="grid grid-cols-2 gap-4 p-6">
        <Card className="p-5">
          <SectionTitle>Appearance</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => theme !== "dark" && toggleTheme()}
              className={cn(
                "flex flex-col items-center gap-2 rounded-xl border p-4 transition",
                theme === "dark" ? "border-app-strong ring-accent" : "border-app hover:border-app-strong"
              )}
            >
              <div className="grid h-16 w-full place-items-center rounded-lg bg-gradient-to-br from-slate-800 to-black">
                <Moon size={20} className="text-amber-400" />
              </div>
              <span className="text-xs font-medium text-app">Dark · Graphite</span>
            </button>
            <button
              onClick={() => theme !== "light" && toggleTheme()}
              className={cn(
                "flex flex-col items-center gap-2 rounded-xl border p-4 transition",
                theme === "light" ? "border-app-strong ring-accent" : "border-app hover:border-app-strong"
              )}
            >
              <div className="grid h-16 w-full place-items-center rounded-lg bg-gradient-to-br from-white to-slate-200">
                <Sun size={20} className="text-amber-500" />
              </div>
              <span className="text-xs font-medium text-app">Light · Frosted</span>
            </button>
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle>Organization</SectionTitle>
          <div className="space-y-2">
            {(me?.orgs ?? []).map((o) => (
              <button
                key={o.id}
                onClick={() => {
                  if (o.id === me?.activeOrgId) return;
                  switchOrg(o.id)
                    .then(() => rehydrateOrgData())
                    .catch(() => pushToast({ title: "Switch failed", body: "Couldn't switch organization — retry", tone: "error" }));
                }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition",
                  me?.activeOrgId === o.id ? "border-app-strong bg-surface-2" : "border-app hover:bg-surface-2/50"
                )}
              >
                <span className="grid h-7 w-7 place-items-center rounded-md text-sm font-semibold" style={{ background: `${accent}22`, color: accent }}>
                  {o.name[0]?.toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm text-app">{o.name}</span>
                  <span className="block text-xs capitalize text-app-2">{o.role}</span>
                </span>
                {me?.activeOrgId === o.id && (
                  <span className="ml-auto h-2 w-2 rounded-full" style={{ background: accent }} />
                )}
              </button>
            ))}
          </div>
        </Card>

        <ProductsCard />
        <SampleDataCard />

        <Card className="col-span-2 p-5">
          <SectionTitle>Preferences</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: User, label: "Account", sub: me?.user.email ?? "—" },
              { icon: Bell, label: "Notifications", sub: "In-app notifications" },
              { icon: Shield, label: "Security", sub: me?.mfaEnabled ? "SSO · MFA enabled" : "SSO · MFA not enabled" },
              { icon: Monitor, label: "Desktop layout", sub: "Resets on reload" },
            ].map((p) => (
              <div key={p.label} className="flex items-center gap-3 rounded-lg border border-app bg-surface-2/40 p-3">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-surface-2">
                  <p.icon size={16} style={{ color: accent }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-app">{p.label}</div>
                  <div className="text-xs text-app-2">{p.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
